import { Button, Container, Popup } from '@mairistem/composants';
import {
	type CodeTva,
	type Exercice,
	type Imputation,
	ImputationSelectionModal,
	type LigneMouvement,
	type Mouvement,
	type Nature,
	type Section,
	type Sens,
	type Tiers,
} from '@mairistem/comptabilite';
import { Icon } from '@mairistem/finances-utils';
import { isNewIdentifiant, newIdentifiant } from '@mairistem/tools';
import List from '@rc-component/virtual-list';
import { isEmpty, maxBy } from 'lodash';
import React from 'react';
import type BaseProfilEcritureMouvement from '../../../../classes/mouvements/profilEcriture/Base/BaseProfilEcritureMouvement';
import type DefaultAttributes from '../../../../interfaces/pieces/mouvements/defaultAttributes';
import type PanelOptions from '../../../../interfaces/pieces/mouvements/panelOptions';
import LignesPiecesControl from './LignesPiecesControl';
import './LignesPieces.css';

const MIN_LIGNES_INFINITE_LIST = 10;

interface SelectedImputation {
	imputation: Partial<Imputation>;
}

interface LignesPiecesProps {
	codeBHM: string;
	codesTva: CodeTva[];
	defaultAttributes: DefaultAttributes;
	deletedLignes: number[];
	exercice: Exercice;
	identifiantCodeServiceTva: number;
	identifiantEntite: number;
	lignes: LigneMouvement[];
	loadingCodesTva: boolean;
	onChange: (data: Pick<Mouvement, 'lignes'>) => void;
	panelOptions: PanelOptions;
	profilEcriture: BaseProfilEcritureMouvement;
	sens: Sens;
	setPanelOptions: React.Dispatch<React.SetStateAction<PanelOptions>>;
	tiers: Tiers;
}

const LignesPieces = ({
	codeBHM,
	codesTva,
	defaultAttributes,
	deletedLignes = [],
	exercice,
	identifiantCodeServiceTva,
	identifiantEntite,
	lignes,
	loadingCodesTva,
	onChange,
	panelOptions,
	profilEcriture,
	sens,
	setPanelOptions,
	tiers,
}: LignesPiecesProps) => {
	const [accordionActive, setAccordionActive] = React.useState(false);
	const [currentEditedLineId, setCurrentEditedLineId] = React.useState(null);
	const [open, setOpen] = React.useState(false);
	// Liste des lignes ouvertes
	const [openedLignes, setOpenedLignes] = React.useState<(number | string)[]>(
		[],
	);

	const isInfiniteList = lignes?.length > MIN_LIGNES_INFINITE_LIST;

	const handleLineChange = (
		identifiantLigne: number | string,
		data: Partial<LigneMouvement>,
	) => {
		onChange({
			lignes: lignes.map((ligne) => ({
				...ligne,
				...(ligne.identifiant === identifiantLigne && data),
			})),
		});
	};

	// Template d'une nouvelle ligne.
	const renderNewLine = (props?: Partial<LigneMouvement>) => {
		const maxIdLigne = maxBy(lignes, (ligne) =>
			Number(ligne?.idLigne),
		)?.idLigne;

		return {
			identifiant: newIdentifiant(),
			identifiantTiers: tiers?.identifiant ?? null,
			idLigne: (Number(maxIdLigne) || 0) + 1,
			tvaRecup: !!identifiantCodeServiceTva,
			...((defaultAttributes ?? {}) as unknown as Partial<LigneMouvement>),
			...props,
		};
	};

	const addNewLine = () => {
		onChange({
			lignes: [...(lignes ?? []), renderNewLine()],
		});
	};

	const removeLine = (identifiantLigne: number | string) => {
		onChange({
			lignes: lignes?.filter((ligne) => ligne.identifiant !== identifiantLigne),
			...(!isNewIdentifiant(identifiantLigne) && {
				deletedLignes: [...deletedLignes, identifiantLigne],
			}),
		});
	};

	const handleOpen = (id: number | string) => {
		setOpen(true);
		setCurrentEditedLineId(id);
	};

	const handleSelection = (imputations: SelectedImputation[]) => {
		const firstImputation = imputations[0].imputation;
		setOpen(false);

		const newLignes: LigneMouvement[] = [];

		if (imputations.length > 1) {
			// Création de nouvelles lignes pour chaque imputation sauf la ligne selectionnée
			let actualMaxIdLigne: number | null = null;
			imputations.slice(1).forEach((imputationData) => {
				const { imputation } = imputationData;
				const nouvelleLigne: LigneMouvement = renderNewLine({
					identifiantImputation: imputation?.identifiant,
					imputation: imputation as Imputation,
					// Mise à jour de l'idLigne pour qu'il soit unique
					...{
						...(actualMaxIdLigne !== null && { idLigne: actualMaxIdLigne + 1 }),
					},
				}) as LigneMouvement;
				newLignes.push(nouvelleLigne);
				actualMaxIdLigne = nouvelleLigne.idLigne;
			});
		}

		onChange({
			lignes: [
				...lignes.map((ligne) => ({
					...ligne,
					...(ligne?.identifiant === currentEditedLineId && {
						identifiantImputation: firstImputation?.identifiant,
						imputation: firstImputation as Imputation,
					}),
				})),
				...newLignes,
			],
		});
	};

	const handleAccordion = () => setAccordionActive((old: boolean) => !old);

	const developedView = () =>
		lignes?.length > 0 &&
		lignes?.length <= MIN_LIGNES_INFINITE_LIST && (
			<Container className="vueDeveloppee" fluid>
				<Button
					content={`Vue ${accordionActive ? 'simple' : 'développée'}`}
					icon={`angle ${accordionActive ? 'up' : 'down'}`}
					labelPosition="left"
					onClick={handleAccordion}
					size="tiny"
				/>
			</Container>
		);

	const renderLigne = (ligne: LigneMouvement, index: number) => (
		<LignesPiecesControl
			accordionActive={accordionActive}
			codeBHM={codeBHM}
			codesTva={codesTva}
			exercice={exercice}
			identifiantEntite={identifiantEntite}
			index={index}
			isInfiniteList={isInfiniteList}
			isLigneOpen={!!openedLignes.find((id) => id === ligne?.identifiant)}
			key={ligne?.identifiant}
			ligne={ligne}
			loadingCodesTva={loadingCodesTva}
			onChange={(data) => handleLineChange(ligne.identifiant, data)}
			onDelete={() => removeLine(ligne.identifiant)}
			panelOptions={panelOptions}
			profilEcriture={profilEcriture}
			sens={sens as Sens}
			setModalOpen={handleOpen}
			setOpenedLignes={setOpenedLignes}
			setPanelOptions={setPanelOptions}
		/>
	);

	return (
		<>
			{developedView()}
			{isInfiniteList ? (
				<List data={lignes} height={500} itemHeight={120} itemKey="identifiant">
					{(ligne, index) => renderLigne(ligne, index)}
				</List>
			) : (
				lignes?.map((ligne, index) => renderLigne(ligne, index))
			)}
			{developedView()}
			<Container textAlign="center">
				<Popup
					content="Nouvelle ligne"
					position="bottom center"
					trigger={
						<Icon
							link
							name="add"
							circular
							size="large"
							onClick={addNewLine}
							data-testid="ajouter-ligne-mouvement"
						/>
					}
				/>
			</Container>
			<ImputationSelectionModal
				articles={profilEcriture?.imputation?.articles}
				chapitre={profilEcriture?.imputation?.chapitre}
				disabledChapitre={!isEmpty(profilEcriture?.imputation?.chapitre)}
				disabledSection={!isEmpty(profilEcriture?.imputation?.section)}
				exercice={exercice}
				identifiantTiers={tiers?.identifiant ?? null}
				nature={profilEcriture?.imputation?.nature as Nature}
				onValidate={handleSelection}
				open={open}
				setOpen={setOpen}
				section={profilEcriture?.imputation?.section as Section}
				sens={sens}
			/>
		</>
	);
};

export default LignesPieces;
